
import { useEffect, useLayoutEffect } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { IMethodData } from '@r1-frontend/ui-react/organisms/Payment/PaymentMethod';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import { pushDataLayer } from '~/src/features/payments-abn/helpers/pushDataLayer';
import { clearPaymentMethods, setPaymentMethod, setPaymentResult, toggleConnectAutoPayStatus, toggleSaveCardStatus } from '~/src/features/payments-abn/store/actionCreators';
import { initialPaymentResult } from '~/src/features/payments-abn/store/reducers';
import { TState } from '~/src/store';

import { ActionSectionContent, ActionSectionTitle, ActionSectionWrapper } from '~/src/features/payments-abn/styled';
import * as ST from './styled';

/**
 * Выводит карточки для переключения способов оплаты
 */
export const PaymentMethodCards = ({ isShowTitle = true }: { isShowTitle?: boolean }): JSX.Element | null => {
    const dispatch = useDispatch();

    const {
        isAuth,
        methods,
        currentMethod,
        result,
    } = useSelector((state: TState) => ({
        isAuth: state.auth.isAuth,
        methods: state.payment.methods,
        currentMethod: state.payment.method,
        result: state.payment.result,
    }), shallowEqual);

    useLayoutEffect(() => {
        dispatch(clearPaymentMethods());
    }, []);

    useEffect(() => {
        if (!currentMethod) {
            const [first] = methods;
            dispatch(setPaymentMethod(first));
        }
    }, [currentMethod, methods]);

    const isTablet = useMatchMedia(`(max-width: ${wideBreakpoints.tablet})`);

    const currentSlideIndex = methods.findIndex(method => method.id === currentMethod?.id);

    const onSelectMethod = (method: IMethodData) => {
        pushDataLayer(`click payment by ${method.methodType}`, '');
        dispatch(setPaymentMethod(method));
        const cardStatus = isAuth && method.methodType === 'new-card';
        dispatch(toggleSaveCardStatus(cardStatus));
        dispatch(toggleConnectAutoPayStatus(cardStatus));

        if (!isEqual(result, initialPaymentResult)) {
            dispatch(setPaymentResult(initialPaymentResult));
        }
    };

    const AdditionalBlock = currentMethod?.additionalBlock;

    if (result.frameUrl) {
        return null;
    }

    return (
        <ActionSectionWrapper>
            {isShowTitle && <ActionSectionTitle>Cпособы оплаты</ActionSectionTitle>}
            <ActionSectionContent className={'ym-hide-content'}>
                <ST.MethodCarousel
                    withoutArrows={true}
                    withoutDots={true}
                    slideWidth={isTablet ? 120 : 136}
                    cellSpacing={0}
                    initialSlideHeight={112}
                    slideIndex={currentSlideIndex}
                    data-test='methods-container'
                >
                    {methods.map(method => {
                        const PayWidget = method.widget;

                        if (!PayWidget) {
                            return null;
                        }

                        return <PayWidget
                            key={method.id}
                            method={method}
                            isChecked={method.id === currentMethod?.id}
                            onSelectMethod={onSelectMethod}
                            data-test='method-item'
                        />;
                    })}
                </ST.MethodCarousel>
                {AdditionalBlock
                    ? <AdditionalBlock/>
                    : null
                }
            </ActionSectionContent>
        </ActionSectionWrapper>
    );
};
